import { type RefObject, useLayoutEffect, useState } from 'react';

import { useContainerWidth } from '@/_shared/hooks/use-container-width';

/**
 * How many chips of a one-line row fit in the cell, leaving room for a trailing "+N" button when they
 * don't all fit. Measured, not estimated: the caller renders a hidden copy of every chip (marked
 * `data-chip`) plus one sample button (marked `data-more`) inside `measureRef`, and this reads their real
 * widths against the container's real width, again on every resize.
 *
 * Until the container has been measured (width 0 — the first paint, or jsdom, which has no layout) every
 * chip is shown and no button: overflowing a cell for one frame is better than hiding columns that fit.
 */
export function useFittedChipCount(
  containerRef: RefObject<HTMLElement | null>,
  measureRef: RefObject<HTMLElement | null>,
  count: number,
): number {
  const width = useContainerWidth(containerRef);
  const [fitted, setFitted] = useState(count);

  useLayoutEffect(() => {
    const measure = measureRef.current;
    if (width === 0 || !measure) {
      setFitted(count);
      return;
    }
    const chips = Array.from(measure.querySelectorAll<HTMLElement>('[data-chip]'));
    const more = measure.querySelector<HTMLElement>('[data-more]');
    const rights = chips.map((chip) => chip.offsetLeft + chip.offsetWidth);
    const first = chips[0];
    const second = chips[1];
    const gap = first && second ? second.offsetLeft - (first.offsetLeft + first.offsetWidth) : 0;
    setFitted(fitChipCount(rights, more?.offsetWidth ?? 0, gap, width));
  }, [width, count, measureRef]);

  return fitted;
}

/**
 * The most chips that fit in `available`, given each chip's right edge on one line (`rights`, from the
 * row's start), the "+N" button's width and the gap between items. All of them when the last edge fits;
 * otherwise the largest `k` with room for `k` chips, a gap and the button — possibly 0, a lone button.
 */
export function fitChipCount(rights: number[], buttonWidth: number, gap: number, available: number): number {
  const last = rights.at(-1);
  if (last === undefined || last <= available) return rights.length;
  for (let k = rights.length - 1; k > 0; k -= 1) {
    const edge = rights[k - 1] ?? 0;
    if (edge + gap + buttonWidth <= available) return k;
  }
  return 0;
}
